/* eslint-disable no-useless-catch */
import moment from 'moment';
import get from 'lodash/get';
import {
  BusinessLevels,
  IRNReportMetadataResponse,
  IRNSummaryDetails,
  ReportFilter,
} from '../../common/types';
import { API_END_POINTS } from '../../config/api-endpoints';
import { getAllDatesBetweenTwoDates } from '../../db/utils';
import {
  createOrUpdateMetadata,
  getMetadataFromDb,
} from '../../db/metadata-db';
import { callApi } from '../../common/api';

const getMetadata = async (
  filter: ReportFilter,
  headers: any
): Promise<IRNReportMetadataResponse> => {
  try {
    const response = await callApi({
      method: 'POST',
      url: API_END_POINTS.EINVOICE.METADATA,
      data: { filter },
      headers,
    });
    return response.data as IRNReportMetadataResponse;
  } catch (e) {
    // console.log(JSON.stringify(e));
    throw e;
  }
};

const getDayWiseMapMetadata = (
  irnMetadataResponses: IRNReportMetadataResponse,
  invoiceStartDate: moment.Moment,
  invoiceEndDate: moment.Moment
): Map<string, IRNSummaryDetails> => {
  const dayWiseIRNReportMetadata = new Map<string, IRNSummaryDetails>();
  const reportsDataPerDay = get(irnMetadataResponses, 'ReportsDataPerDay');
  if (reportsDataPerDay) {
    for (const reportsData of reportsDataPerDay) {
      const irnMetadata: IRNSummaryDetails = {
        totalPendingIrnGeneration: get(
          reportsData,
          'ReportsData.NotGenerated.Count',
          0
        ),
        totalNoOfInvoices: get(reportsData, 'ReportsData.Total.Count', 0),
        totalIrnGenerated: get(reportsData, 'ReportsData.Generated.Count', 0),
        totalIrnFailed: get(reportsData, 'ReportsData.Failed.Count', 0),
        totalIrnCancelled: get(reportsData, 'ReportsData.Cancelled.Count', 0),
      };
      dayWiseIRNReportMetadata.set(reportsData.Date, irnMetadata);
    }
  }

  const dateRange = getAllDatesBetweenTwoDates(
    invoiceStartDate,
    invoiceEndDate
  );

  for (const date of dateRange) {
    const dateString = date.format('DD/MM/YYYY');
    if (dayWiseIRNReportMetadata.has(dateString)) continue;
    dayWiseIRNReportMetadata.set(dateString, {
      totalIrnCancelled: 0,
      totalIrnFailed: 0,
      totalIrnGenerated: 0,
      totalNoOfInvoices: 0,
      totalPendingIrnGeneration: 0,
    });
  }

  return dayWiseIRNReportMetadata;
};

const getMetadataFromApi = async (
  invoiceStartDate: string,
  invoiceEndDate: string,
  orgId: string,
  businessLevel: BusinessLevels,
  business: string,
  businessId: string,
) => {
  const filter: ReportFilter = {
    invoiceStartDate,
    invoiceEndDate,
  };
  let headers: any = {
    'node-id': businessId,
    'node-type': businessLevel,
  };
  switch (businessLevel) {
    case BusinessLevels.PAN:
      headers = {
        ...headers,
        PAN: business,
      };
      break;
    case BusinessLevels.GSTIN:
      headers = {
        ...headers,
        GSTIN: business,
      };
      break;
    case BusinessLevels.BRANCH:
      headers = {
        ...headers,
        BRANCH: business,
      };
      break;
  }
  const irnMetadataResponses = await getMetadata(filter, headers);
  const startDate = moment(filter.invoiceStartDate, 'YYYY-MM-DD HH:mm:ss');
  const endDate = moment(filter.invoiceEndDate, 'YYYY-MM-DD HH:mm:ss');
  const irnMetadata = getDayWiseMapMetadata(
    irnMetadataResponses,
    startDate,
    endDate
  );
  createOrUpdateMetadata(irnMetadata, orgId);
  return irnMetadata;
};

export const getIRNSummaryDayWise = async (
  invoiceEndDate: string,
  invoiceStartDate: string,
  orgId: string,
  businessLevel: BusinessLevels,
  business: string,
  businessId: string
): Promise<Map<string, IRNSummaryDetails>> => {
  try {
    const responseFromDb = await getMetadataFromDb(
      invoiceStartDate,
      invoiceEndDate,
      orgId
    );
    const responseFromApi = getMetadataFromApi(
      invoiceStartDate,
      invoiceEndDate,
      orgId,
      businessLevel,
      business,
      businessId
    );
    if (responseFromDb && responseFromDb.size !== 0) {
     //  console.log('From db');
      return responseFromDb;
    }
    // console.log('From api');
    const response = await responseFromApi;
    return response;
  } catch (error) {
    // console.log(error);
    throw error;
  }
};
